import { useState, useEffect } from 'react';
import { FiX, FiClock, FiCalendar } from 'react-icons/fi';
import { timeEntriesAPI } from '../../services/api';

const TimeEntriesModal = ({ isOpen, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTodayEntries();
    }
  }, [isOpen]);

  const loadTodayEntries = async () => {
    try {
      setIsLoading(true);
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      const data = await timeEntriesAPI.getAll({
        start_date: startOfDay.toISOString(),
        end_date: endOfDay.toISOString()
      });

      setEntries(data || []);
    } catch (error) {
      console.error('Error loading entries:', error);
      setEntries([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDuration = (minutes) => {
    if (minutes == null) return '--:--:--';  // Only return placeholder if null or undefined
    const totalSeconds = Math.round(minutes * 60);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const getTotalMinutes = () => {
    return entries.reduce((sum, entry) => {
      // Use nullish coalescing to only default null/undefined, not 0
      const minutes = entry.total_minutes ?? 0;
      return sum + minutes;
    }, 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FiCalendar className="h-5 w-5 text-white" />
            <h2 className="text-lg font-semibold text-white">
              Registros de Hoy
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-1 rounded transition-colors"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">
              Cargando...
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8">
              <FiClock className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No hay registros para hoy</p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((entry, index) => (
                <div
                  key={entry.id}
                  className={`border rounded-lg p-3 ${
                    entry.clock_out_time
                      ? 'bg-green-50 border-green-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Sesión {index + 1}
                    </span>
                    {!entry.clock_out_time && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        En curso
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs">Inicio</p>
                      <p className="font-medium text-gray-900">
                        {formatTime(entry.clock_in_time)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Fin</p>
                      <p className="font-medium text-gray-900">
                        {formatTime(entry.clock_out_time)}
                      </p>
                    </div>
                  </div>

                  {entry.clock_out_time && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Duración</span>
                        <span className="text-sm font-semibold text-green-700">
                          {formatDuration(entry.total_minutes)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with total */}
        {entries.length > 0 && (
          <div className="bg-gray-50 px-4 py-3 border-t">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Total del día</span>
              <span className="text-lg font-bold text-blue-600">
                {formatDuration(getTotalMinutes())}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeEntriesModal;
